import { AgeIndicator } from '@/components/dashboard/AgeIndicator';
import { UeberfaelligBadge } from '@/components/detail/UeberfaelligBadge';
import { datumDe, datumKompaktDe } from '@/lib/format';
import { zeileIstUeberfaellig } from '@/lib/ueberfaellig';
import type { BestellungZeile } from '@/types/dashboard';

/**
 * Der zugesagte Abholtermin einer Bestellung — Karte wie Tabelle.
 *
 * DREI FÄLLE, und der dritte ist mit A37.1 dazugekommen:
 *   1. läuft noch, Termin verstrichen  → das Merkzeichen „überfällig seit"
 *   2. läuft noch, Termin steht bevor  → Restlauf mit Ampelpunkt
 *   3. ABGESCHLOSSEN                   → nur das Datum, ohne Wertung
 *
 * WARUM ES DIESE DATEI GIBT — ein Fehler von mir, den eine grüne
 * Gegenprobe aufgedeckt hat:
 *
 * Beim ersten Anlauf habe ich nur das Merkzeichen unterdrückt. Damit fiel
 * eine abgeholte Bestellung in Fall 2 — und der AgeIndicator sagt bei
 * einem negativen Restlauf „überfällig (vor 5 Tagen)". Die falsche
 * Aussage war also nicht weg, sie war nur umgezogen. Gemerkt habe ich es
 * nicht, weil meine Zusicherung nach „überfällig seit" suchte, dem
 * Wortlaut des Merkzeichens, statt nach dem Wort, das der Betreiber
 * gemeldet hatte.
 *
 * Deshalb steht die Fallunterscheidung jetzt an EINER Stelle statt als
 * gleichlautender Dreizeiler in Karte und Tabelle. Zwei Kopien hätten
 * denselben Weg noch einmal gefunden.
 *
 * Der abgeschlossene Vorgang bekommt bewusst KEINE Ampelfarbe: Er ist
 * weder dringend noch entspannt, er ist fertig. Das Datum bleibt
 * trotzdem stehen — wer die Zeile ansieht, soll nicht rätseln, ob es je
 * einen Termin gab.
 */
export function AbholTermin({
  zeile,
  restOffen
}: {
  zeile: BestellungZeile;
  /**
   * Was die Mahnung an Rest nennen soll (A57.2), oder null.
   *
   * PFLICHT und nicht optional, obwohl beide Aufrufstellen im selben
   * Verzeichnis liegen — weil die beiden Stellen sich hier UNTERSCHEIDEN
   * und das sichtbar bleiben soll. Auf der Karte ist Platz, in der
   * Termin-Spalte der Tabelle nicht; die Zahlen dazu stehen an den
   * Aufrufstellen. Eine Eigenschaft mit Vorgabewert haette diesen
   * Unterschied zu einem Versehen gemacht, das niemandem auffaellt.
   */
  restOffen: number | null;
}) {
  const { abholdatum_zugesagt: datum, tage_bis_zugesagt: tage } = zeile;
  if (datum === null || tage === null) return null;

  if (zeile.zustand.ist_endzustand) {
    return (
      <span className="whitespace-nowrap text-ink-faint" title={`zugesagt war: ${datumDe(datum)}`}>
        Termin {datumKompaktDe(datum)}
      </span>
    );
  }

  return zeileIstUeberfaellig(zeile) ? (
    <UeberfaelligBadge
      zugesagt={datum}
      istEndzustand={zeile.zustand.ist_endzustand}
      restOffen={restOffen}
    />
  ) : (
    <AgeIndicator tage={tage} modus="bis" datum={datum} />
  );
}
